/**
 * PasswordResetDialog Component
 *
 * Two-step dialog for resetting user passwords:
 * 1. Confirmation step with user details
 * 2. Success step displaying temporary password with copy functionality
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { Copy24Regular, Checkmark24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { UserApiService } from '../../../services/userApi';
import { Alert } from '../../common/Alert';
import type { TenantUser, PasswordResetResult } from '../../../types/user';

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  passwordDisplay: {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase400,
    userSelect: 'all',
    wordBreak: 'break-all',
  },
  instructions: {
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontSize: tokens.fontSizeBase300,
  },
});

export interface PasswordResetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: TenantUser | null;
  tenantId: string;
}

export function PasswordResetDialog({
  isOpen,
  onClose,
  user,
  tenantId,
}: PasswordResetDialogProps) {
  const styles = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<PasswordResetResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
      setError(null);
      setResetResult(null);
      setCopied(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsLoading(false);
    setError(null);
    setResetResult(null);
    setCopied(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await UserApiService.resetPassword(tenantId, user.id, true);
      setResetResult(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!resetResult) return;

    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the text for manual copy
      const passwordElement = document.querySelector('[data-testid="temporary-password"]');
      if (passwordElement) {
        const range = document.createRange();
        range.selectNodeContents(passwordElement);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface data-testid="password-reset-dialog" style={{ maxWidth: '600px' }}>
        <DialogBody>
          <DialogTitle>
            {resetResult ? 'Password Reset Successful' : 'Reset User Password'}
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {error && (
              <Alert intent="error" data-testid="password-reset-error">
                <Text>{error}</Text>
              </Alert>
            )}

            {!resetResult ? (
              // Confirmation Step
              <>
                <Text>
                  Are you sure you want to reset the password for{' '}
                  <Text weight="semibold">{user.display_name}</Text> (
                  {user.user_principal_name})?
                </Text>
                <Text size={200}>
                  A temporary password will be generated and the user will be required to
                  change it on their next sign-in.
                </Text>
              </>
            ) : (
              // Success Step
              <>
                <Text>
                  Password has been successfully reset for{' '}
                  <Text weight="semibold">{user.display_name}</Text>.
                </Text>

                <div>
                  <Text weight="semibold" block style={{ marginBottom: '8px' }}>
                    Temporary Password:
                  </Text>
                  <div className={styles.passwordDisplay} data-testid="temporary-password">
                    {resetResult.temporary_password}
                  </div>
                </div>

                <div className={styles.instructions}>
                  <Text size={200}>
                    <strong>Important:</strong> Save this password now. It will not be shown
                    again. The user must change this password on their next sign-in.
                  </Text>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {!resetResult ? (
              <>
                <Button
                  appearance="secondary"
                  onClick={handleClose}
                  disabled={isLoading}
                  data-testid="password-reset-cancel"
                >
                  Cancel
                </Button>
                <AsyncButton
                  appearance="primary"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  loading={isLoading}
                  loadingText="Resetting..."
                  data-testid="password-reset-confirm"
                >
                  Reset Password
                </AsyncButton>
              </>
            ) : (
              <>
                <Button
                  appearance="secondary"
                  icon={copied ? <Checkmark24Regular /> : <Copy24Regular />}
                  onClick={handleCopy}
                  data-testid="password-reset-copy"
                >
                  {copied ? 'Copied!' : 'Copy Password'}
                </Button>
                <Button
                  appearance="primary"
                  icon={<Dismiss24Regular />}
                  onClick={handleClose}
                  data-testid="password-reset-close"
                >
                  Close
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
